/**
 * WindowLayer — mounts all open floating windows + the minimized dock.
 * Renders at a high z-index root but with NO backdrop, so the main
 * dashboard stays fully interactive underneath.
 */
import React from "react";
import { Maximize2, X } from "lucide-react";
import { useFloatingWindows } from "@/lib/floatingWindows";
import FloatingWindow from "@/components/nds/windows/FloatingWindow";
import QuickEditContent from "@/components/nds/windows/QuickEditContent";
import QuickFormsContent from "@/components/ops/QuickFormsContent";
import CardConfigContent from "@/components/ops/CardConfigContent";
import StepCoachingContent from "@/components/nds/windows/StepCoachingContent";
import {
  BriefIntakeContent,
  NewContactContent,
  SendSmsContent,
  OpenModuleContent,
} from "@/components/nds/windows/ActionContent";

const KIND_MAP = {
  quickEdit:     { Content: QuickEditContent,     kicker: "// QUICK EDIT · SCHEMA" },
  brief_intake:  { Content: BriefIntakeContent,   kicker: "// NEW FRICTION AUDIT BRIEF" },
  new_contact:   { Content: NewContactContent,    kicker: "// NEW CRM CONTACT" },
  send_sms:      { Content: SendSmsContent,       kicker: "// SEND SMS" },
  open_module:   { Content: OpenModuleContent,    kicker: "// OPEN MODULE" },
  step_coaching: { Content: StepCoachingContent,  kicker: "// STEP COACHING" },
  quickForms:    { Content: QuickFormsContent,    kicker: "// QUICK ENTRY" },
  cardConfig:    { Content: CardConfigContent,    kicker: "// CONFIGURE CARD" },
};

const titleFor = (win) => {
  if (win.kind === "quickEdit") {
    const kindLabel = { kpi: "KPI Card", step: "Revenue Step", capability: "Capability Tile" }[win.payload?.kind] || win.payload?.kind;
    return `Edit · ${kindLabel}`;
  }
  if (win.kind === "step_coaching") {
    return `Step ${win.payload?.step?.id || ""} · ${win.payload?.step?.title || ""}`;
  }
  return win.payload?.title || KIND_MAP[win.kind]?.kicker || "Window";
};

const subtitleFor = (win) => {
  if (win.kind === "quickEdit") return `${win.payload?.verticalId || ""} · ${win.payload?.itemId || ""}`;
  return win.payload?.description || null;
};

/**
 * popoutHrefFor — maps a window to its dedicated full-page URL so the user
 * can "pop out" a popup into its own browser tab. Returns null when there
 * is no matching standalone page (button then hides).
 */
const popoutHrefFor = (win) => {
  switch (win.kind) {
    case "brief_intake":  return "/new-brief";                      // full-page Friction Audit
    case "new_contact":   return "/admin/contacts";                 // CRM contacts list — new form inline
    case "send_sms":      return "/admin/sms";                      // SMS console
    case "open_module":   return win.payload?.href || null;
    case "quickForms":    return win.payload?.href || null;
    case "cardConfig":    return "/admin/forms";
    case "quickEdit":     return "/admin/nds";                      // full JSON schema editor
    case "step_coaching": return `/admin/briefs?step=${encodeURIComponent(win.payload?.step?.id || "")}`;
    default:              return null;
  }
};

export default function WindowLayer() {
  const { windows, restoreWindow, closeWindow } = useFloatingWindows();
  const open = windows.filter((w) => !w.minimized);
  const minimized = windows.filter((w) => w.minimized);

  return (
    <>
      {/* Floating windows layer — pointer-events-none root so board underneath
          stays interactive; individual windows re-enable pointer events. */}
      <div className="fixed inset-0 pointer-events-none z-[45]" data-testid="nds-window-layer">
        {open.map((win) => {
          const entry = KIND_MAP[win.kind];
          if (!entry) return null;
          const { Content } = entry;
          return (
            <FloatingWindow
              key={win.id}
              win={win}
              kicker={entry.kicker}
              title={titleFor(win)}
              subtitle={subtitleFor(win)}
              popoutHref={popoutHrefFor(win)}
            >
              <Content win={win} />
            </FloatingWindow>
          );
        })}
      </div>

      {/* Minimized dock */}
      {minimized.length > 0 && (
        <div
          className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[46] flex items-center gap-2 px-3 py-2 bg-[#0B1220] border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
          data-testid="nds-window-dock"
        >
          <span className="font-mono text-[0.55rem] tracking-[0.22em] text-nex-muted uppercase">Minimized</span>
          {minimized.map((win) => (
            <div
              key={win.id}
              className="inline-flex items-center gap-1 pl-2 pr-1 border border-white/10 hover:border-nex-gold/50 transition-colors"
              data-testid={`nds-window-dock-item-${win.kind}`}
            >
              <button
                type="button"
                onClick={() => restoreWindow(win.id)}
                className="inline-flex items-center gap-1.5 py-1 text-nex-silver hover:text-nex-gold font-mono text-[0.6rem] tracking-[0.14em] uppercase max-w-[180px] truncate"
                title={titleFor(win)}
              >
                <Maximize2 className="w-3 h-3" /> {titleFor(win)}
              </button>
              <button
                type="button"
                onClick={() => closeWindow(win.id)}
                className="w-5 h-5 grid place-items-center text-nex-muted hover:text-nex-silver"
                aria-label="Close"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
